import React from "react";
import { LanguageCode, Profile, VideoAd } from "../../models";
import { FormControl, FormControlLabel, InputLabel, MenuItem, NativeSelect, Select, SelectChangeEvent, Switch, TextField } from "@mui/material";

interface VideoAdFormProps {
  videoAd: Omit<VideoAd, "id" | "createdAt" | "updatedAt">;
  languageCodes: LanguageCode[];
  profiles: Profile[];
  onVideoAdChange: (videoAd: Omit<VideoAd, "id" | "createdAt" | "updatedAt">) => void;
  errors?: Record<keyof VideoAd, string>;
  children?: React.ReactNode;
}

export const VideoAdForm: React.FC<VideoAdFormProps> = ({ videoAd, languageCodes, profiles, onVideoAdChange, errors, children }) => {
  function handleVideoAdChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | SelectChangeEvent<unknown>) {
    const field = event.target.name;

    if (["duration", "viewCount", "likeCount", "dislikeCount", "commentCount"].includes(field)) {
      if (parseInt(event.target.value as string) < 0) {
        event.target.value = 0;
      }
    }

    onVideoAdChange({ ...videoAd, [field]: event.target.value });
  }

  function handleSwitchChange(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    const field = event.target.name;
    onVideoAdChange({ ...videoAd, [field]: checked });
  }

  return (
    <>
      <div className="post-panel" style={{ gap: "1rem" }}>
        <TextField error={!!errors?.name} helperText={errors?.name} className="spacer" variant="outlined" label="Name" name="name" value={videoAd.name} onChange={handleVideoAdChange} />
        <TextField error={!!errors?.title} helperText={errors?.title} className="spacer" variant="outlined" label="Title" name="title" value={videoAd.title} onChange={handleVideoAdChange} />
        <TextField error={!!errors?.contentUrl} helperText={errors?.contentUrl} className="spacer" variant="outlined" label="Content URL" name="contentUrl" value={videoAd.contentUrl} onChange={handleVideoAdChange} />
        <FormControlLabel className="spacer"
          control={
            <Switch
              checked={videoAd.skippable}
              onChange={handleSwitchChange}
              name="skippable"
              color="primary"
            />
          }
          label="Skippable"
        />
        <TextField inputProps={{ min: 0, type: "number" }} variant="outlined" label="Skippable after seconds" name="duration" value={videoAd.skippableAfterSeconds} onChange={handleVideoAdChange} />
        <FormControlLabel className="spacer"
          control={
            <Switch
              checked={videoAd.muted}
              onChange={handleSwitchChange}
              name="muted"
              color="primary"
            />
          }
          label="Muted"
        />

        <FormControlLabel className="spacer"
          control={
            <Switch
              checked={videoAd.viewabilityTracking}
              onChange={handleSwitchChange}
              name="viewabilityTracking"
              color="primary"
            />
          }
          label="Viewability Tracking"
        />

        <FormControlLabel className="spacer"
          control={
            <Switch
              checked={videoAd.processed}
              onChange={handleSwitchChange}
              name="processed"
              color="primary"
            />
          }
          label="Processed"
        />
      </div>
      <div>Preview coming soon</div>
      <div className="unique-panel" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <FormControl className="spacer" variant="outlined">
          <InputLabel>Type</InputLabel>
          <Select
            value={videoAd.type}
            onChange={handleVideoAdChange}
            label="Type"
            name="type"
          >
            <MenuItem value="pre_roll">Pre Roll</MenuItem>
            <MenuItem value="mid_roll">Mid Roll</MenuItem>
            <MenuItem value="post_roll">Post Roll</MenuItem>
          </Select>
        </FormControl>
        <FormControl className="spacer" variant="outlined">
          <InputLabel variant="outlined" htmlFor="profileId">Profile</InputLabel>
          <NativeSelect
            value={videoAd.profileId}
            onChange={handleVideoAdChange}
            inputProps={{
              name: 'age',
              id: 'profileId',
            }}
            name="profileId"
          >
            {profiles.map((profile) => (
              <option value={profile.id} key={profile.id}>{profile.name}</option >
            ))}
          </NativeSelect>
        </FormControl>
        <FormControl className="spacer" variant="outlined">
          <InputLabel>Language Code</InputLabel>
          <Select
            value={videoAd.languageCodeId}
            onChange={handleVideoAdChange}
            label="Language Code"
            name="languageCodeId"
          >
            {languageCodes.map((languageCode) => (
              <MenuItem value={languageCode.id} key={languageCode.id}>{languageCode.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {children}
      </div>
    </>
  );
};
